import { Link, useLocation , useNavigate} from "react-router-dom";
import Image from "@/components/ui/optimized-image";
import { Button } from "@/components/ui/button";
 import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuSeparator,
   DropdownMenuTrigger,
 } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/hooks/use-auth";
import { SearchBar } from "@/components/search-bar";
import {
  Menu,
  ChevronDown,
  User,
  LogOut,
  BookOpen,
  Users as UsersIcon,
  HelpCircle,
  Search,
  Trophy,
  FolderKanban,
  MessageSquare,
  GraduationCap,
  FileText,
  Building2,
  LayoutDashboard,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { getUserProfile } from "@/lib/profile";
import { ThemeToggle } from "@/components/theme-toggle";
import { useTheme } from "@/components/theme-provider";
import { LanguageToggle } from "@/components/language-toggle";
import { useI18n } from "@/lib/i18n";
import { useAdmin } from "@/lib/hooks/use-admin";
import { useMentor } from "@/lib/hooks/use-mentor";
import { NotificationsDropdown } from "@/components/notifications-dropdown";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
 
 export function Header({ mode }: { mode?: "academy" | "business" } = {}) {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useI18n();
  const { theme } = useTheme();
  const { isAdmin } = useAdmin();
  const { isMentor, loading: mentorLoading } = useMentor();
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [username, setUsername] = useState<string>("");
  const [searchOpen, setSearchOpen] = useState(false);

  // Fetch user profile
  useEffect(() => {
    const loadProfile = async () => {
      if (!user || loading) return;
      
      try {
        const profile = await getUserProfile(user.id, user.email || undefined);
        if (profile) {
          setProfileImageUrl(profile.image_url || null);
          // Use username first, then email prefix (never show first/last name)
          const displayName = profile.username 
            || profile.email?.split("@")[0] 
            || "User";
          setUsername(displayName);
        } else {
          // Fallback to user object data
          const displayName = user.username 
            || user.email?.split("@")[0] 
            || "User";
          setUsername(displayName);
          setProfileImageUrl(user.imageUrl || null);
        }
      } catch {
        const displayName = user.username 
          || user.email?.split("@")[0] 
          || "User";
        setUsername(displayName);
        setProfileImageUrl(user.imageUrl || null);
      }
    };

    loadProfile();

    // Listen for profile updates from other components
    const handleProfileUpdate = () => {
      loadProfile();
    };
    window.addEventListener('profile-updated', handleProfileUpdate);
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate);
    };
  }, [user, loading]);


  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/";
  };

  const platformMode: "academy" | "business" =
    mode ?? (pathname.startsWith("/projects") || pathname.startsWith("/grants") ? "business" : "academy");

  const baseNavItems =
    platformMode === "business"
      ? [
          { href: "/projects", label: "Projects" },
          { href: "/jobs", label: "Jobs" },
          { href: "/raffle", label: t("nav.raffle") },
        ]
      : [
          { href: "/projects", label: "Projects" },
          { href: "/jobs", label: "Jobs" },
          { href: "/achievements", label: t("nav.achievements") },
          { href: "/raffle", label: t("nav.raffle") },
          { href: "/faucet", label: t("nav.faucet") },
        ];

  const navItems = isAdmin
    ? [...baseNavItems, { href: "/admin", label: t("nav.admin") }]
    : baseNavItems;

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname === href || pathname?.startsWith(href + "/");
  };

  const userEmail = user?.email || "";
  const userInitials = userEmail ? userEmail.charAt(0).toUpperCase() : "U";
  const userAvatar = profileImageUrl || null;
 
   return (
     <header
      className="app-site-header fixed top-0 left-0 right-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur-sm safe-area-inset-top"
       style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50 }}
     >
      <div className="container mx-auto flex h-14 sm:h-16 items-center gap-2 sm:gap-3 px-3 sm:px-5 md:px-6">
        {/* Mobile Menu (hamburger) - Left side */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="lg:hidden h-9 w-9 p-0">
              <Menu className="h-5 w-5" />
              <span className="sr-only">{t("header.menu")}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {navItems.map((item) => (
              <DropdownMenuItem key={item.href} asChild>
                <Link to={item.href}>{item.label}</Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            {!user ? (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/auth/sign-in">
                    <User className="mr-2 h-4 w-4" />
                    {t("header.signIn")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/auth/sign-up">{t("header.getStarted")}</Link>
                </DropdownMenuItem>
              </>
            ) : (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/me">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Me
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={`/profile/${user?.username || user?.id || ''}`}>
                    <User className="mr-2 h-4 w-4" />
                    {t("header.profile")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={`/cv/${user?.username || user?.id || ''}`}>
                    <FileText className="mr-2 h-4 w-4" />
                    {t("header.myCv")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("header.signOut")}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Logo + Study */}
          <Link
            to="/"
          className="flex items-center gap-2 shrink-0 transition-all duration-200 hover:opacity-80"
          >
            <Image
              src="/SuperCrest.svg"
              alt="SuperStudy logo"
              width={32}
              height={32}
              className="h-8 w-8 rounded-md object-contain"
              style={theme === "light" ? { filter: "brightness(0)" } : undefined}
              priority
            />
          <span className="text-sm sm:text-base font-semibold tracking-tight">SuperStudy</span>
          </Link>

        {/* Separator */}
        <div className="hidden lg:block h-6 w-px bg-border" />

        {/* Navigation Links */}
        <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
          {navItems.map((item) => (
            <Link
              key={item.href} to={item.href}
              className={cn(
                "text-sm font-medium transition-colors duration-200 tracking-tight",
                isActive(item.href)
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {item.label}
            </Link>
          ))}
          {user && (
            <Link
              to="/me"
              className={cn(
                "text-sm font-medium transition-colors duration-200 tracking-tight inline-flex items-center gap-1.5",
                isActive("/me")
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <LayoutDashboard className="h-4 w-4 shrink-0" />
              Me
            </Link>
          )}
        </nav>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Search Bar - Desktop only */}
        <div className="hidden lg:flex items-center w-full max-w-md mx-4">
          <SearchBar />
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {!loading && (
            <>
              {/* Search Icon - Mobile only */}
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden h-9 w-9 p-0"
                onClick={() => setSearchOpen(true)}
              >
                <Search className="h-5 w-5" />
                <span className="sr-only">{t("header.search")}</span>
              </Button>

              {/* Mobile Search Dialog */}
              <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{t("header.search")}</DialogTitle>
                    <DialogDescription className="sr-only">
                      {t("header.searchDescription")}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4">
                    <SearchBar />
                  </div>
                </DialogContent>
              </Dialog>

              {/* Theme Toggle - always visible */}
              <ThemeToggle />
              <LanguageToggle />

              {user ? (
                <>
                  {/* Notifications */}
                  <NotificationsDropdown userId={user.id} />

                  {/* Profile Picture + Username + Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto px-2 py-1.5 gap-2 hover:bg-muted"
                      >
                        {userAvatar ? (
                          <div className="relative h-8 w-8 rounded-full overflow-hidden">
                            <Image
                              src={userAvatar}
                              alt={username}
                              fill
                              className="object-cover"
                            />
                          </div>
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                            {userInitials}
                          </div>
                        )}
                        <span className="hidden sm:block text-sm font-medium max-w-[100px] truncate">
                          {username}
                        </span>
                        <ChevronDown className="h-4 w-4 hidden sm:block text-muted-foreground" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-1.5">
                        <p className="text-sm font-medium">{username}</p>
                        <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/me" className="flex items-center w-full">
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          Me
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/profile/${user?.username || user?.id || ''}`} className="flex items-center w-full">
                          <User className="mr-2 h-4 w-4" />
                          {t("header.profile")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/cv/${user?.username || user?.id || ''}`} className="flex items-center w-full">
                          <FileText className="mr-2 h-4 w-4" />
                          {t("header.myCv")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/achievements" className="flex items-center w-full">
                          <Trophy className="mr-2 h-4 w-4" />
                          {t("header.achievement")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/my-classes" className="flex items-center w-full">
                          <BookOpen className="mr-2 h-4 w-4" />
                          {t("header.myClasses")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {/* Mentor option - only visible to mentors */}
                      {isMentor && (
                        <DropdownMenuItem asChild>
                          <Link to="/mentor" className="flex items-center w-full">
                            <GraduationCap className="mr-2 h-4 w-4" />
                            {t("header.mentor")}
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {isMentor && (
                        <DropdownMenuItem asChild>
                          <Link to="/mentor/academies" className="flex items-center w-full">
                            <Building2 className="mr-2 h-4 w-4" />
                            Academies
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={handleSignOut}
                        className="text-red-600 focus:bg-red-50 dark:text-red-400 dark:focus:bg-red-950/40"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {t("header.signOut")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  {/* Sign In / Get Started for non-authenticated users */}
                  <div className="hidden sm:flex items-center gap-2">
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/auth/sign-in">{t("header.signIn")}</Link>
                    </Button>
                    <Button size="sm" asChild>
                      <Link to="/auth/sign-up">{t("header.getStarted")}</Link>
                    </Button>
                  </div>
                </>
              )}
            </>
          )}

        </div>
      </div>
    </header>
  );
}
